import {
  type CostEstimate,
  clientCostEstimateShowQueryKey,
  clientCostEstimateSyncStatusOptions,
  clientCostEstimateSyncStatusQueryKey,
  useClientCostEstimateShowQuery,
  useClientCostEstimateSyncInsuranceDataMutation,
} from '@cartwheel/api/v1'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Announcement, AnnouncementType } from '../announcement'
import * as Card from '../card'
import { Spinner } from '../spinner'
import { CostEstimateModal } from './cost-estimate-modal'
import { EstimateDisplay } from './estimate-display'
import { insuranceTypeLabel } from './insurance-type-label'
import { SendToFamilyButton } from './send-to-family-button'
import { extractSyncErrorDetails, SyncErrorsBanner } from './sync-errors-banner'
import { SyncInsuranceDataButton } from './sync-insurance-data-button'
import type { CostEstimateInteraction } from './types'

interface CostEstimateCardProps {
  clientId: string
  showSendButton?: boolean
  onEstimateChange?: (estimate: CostEstimate | null) => void
  onSyncFinished?: () => void
  onInteraction?: (interaction: CostEstimateInteraction) => void
}

const SIX_MONTHS_MS = 1000 * 60 * 60 * 24 * 30 * 6

export function CostEstimateCard({
  clientId,
  showSendButton = true,
  onEstimateChange,
  onSyncFinished,
  onInteraction,
}: CostEstimateCardProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncErrors, setSyncErrors] = useState<string[] | null>(null)
  const queryClient = useQueryClient()

  const { data, isPending } = useClientCostEstimateShowQuery({
    path: { client_id: clientId },
  })

  // Poll sync_status while a sync is in progress; stop on success or error so we
  // don't hammer a 422 (e.g. client has no apero_id and the job fails immediately).
  const syncStatus = useQuery({
    ...clientCostEstimateSyncStatusOptions({ path: { client_id: clientId } }),
    enabled: isSyncing,
    refetchInterval: (query) => {
      if (query.state.error) return false
      if (query.state.data?.status === 'succeeded') return false
      return 2000
    },
    retry: false,
  })

  useEffect(() => {
    if (!isSyncing) return
    if (syncStatus.data?.status === 'succeeded') {
      queryClient.invalidateQueries({
        queryKey: clientCostEstimateShowQueryKey({ path: { client_id: clientId } }),
      })
      onSyncFinished?.()
      setIsSyncing(false)
    } else if (syncStatus.error) {
      setSyncErrors(extractSyncErrorDetails(syncStatus.error))
      onSyncFinished?.()
      setIsSyncing(false)
    }
  }, [isSyncing, syncStatus.data, syncStatus.error, queryClient, clientId, onSyncFinished])

  useEffect(() => {
    if (!onEstimateChange || !data) return
    onEstimateChange(data.id ? data : null)
  }, [data, onEstimateChange])

  const triggerSync = useClientCostEstimateSyncInsuranceDataMutation({
    onSuccess: () => {
      // Drop any cached error from a previous attempt so the polling query
      // starts from a clean slate — otherwise the refetchInterval immediately
      // returns false on the stale error and we never re-poll.
      queryClient.removeQueries({
        queryKey: clientCostEstimateSyncStatusQueryKey({ path: { client_id: clientId } }),
      })
      setSyncErrors(null)
      setIsSyncing(true)
    },
    onError: (error) => {
      setSyncErrors(extractSyncErrorDetails(error))
      onSyncFinished?.()
    },
  })

  const handleSync = () => {
    onInteraction?.({
      type: 'sync_requested',
      mode: data?.last_synced_at ? 'refresh' : 'initial',
    })
    triggerSync.mutate({ path: { client_id: clientId } })
  }

  if (isPending || !data) {
    return (
      <Card.Root variant="outline" className="relative mb-4 py-8 px-6">
        <Spinner fillContainer />
      </Card.Root>
    )
  }

  // Mirrors the Rails sync_loading.turbo_stream — while the sync job runs, the
  // entire card is replaced by a centered "Pulling insurance data..." spinner.
  if (isSyncing) {
    return (
      <Card.Root variant="outline" className="relative mb-4 py-8 px-6">
        <div className="flex flex-col items-center justify-center gap-4 py-8">
          <Spinner size="lg" />
          <h3 className="font-ginto-nord-md text-charcoal text-lg font-semibold">
            Pulling insurance data...
          </h3>
        </div>
      </Card.Root>
    )
  }

  const estimate = data
  const hasSavedEstimate = estimate.id !== null
  const isStale =
    hasSavedEstimate && estimate.created_at !== null
      ? Date.now() - new Date(estimate.created_at).getTime() > SIX_MONTHS_MS
      : false
  const showEdit = hasSavedEstimate && !isStale
  const showCreate = !hasSavedEstimate || isStale
  const editorMode = showEdit ? 'edit' : 'create'
  const handleEditorOpen = () => {
    onInteraction?.({ type: 'editor_opened', mode: editorMode })
    setModalOpen(true)
  }

  const isMedicaidWithSessionLimit =
    estimate.estimate_type === 'medicaid' && estimate.editable.session_limit !== null

  return (
    <>
      <Card.Root variant="outline" className="relative mb-4 py-4 px-6 grid gap-2 overflow-visible">
        {isMedicaidWithSessionLimit && (
          <div className="mb-4">
            <Announcement
              type={AnnouncementType.Warning}
              title="When session limits apply, Care Coordinator review is required"
            />
          </div>
        )}

        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-ginto-nord-md text-charcoal text-lg font-semibold">
              Cost Estimate Details
            </h4>
            <div className="mt-1">
              {showEdit ? (
                <button
                  type="button"
                  onClick={handleEditorOpen}
                  className="text-sm font-medium underline text-indigo cursor-pointer"
                >
                  Edit estimate
                </button>
              ) : showCreate ? (
                <button
                  type="button"
                  onClick={handleEditorOpen}
                  className="text-sm font-medium underline text-indigo cursor-pointer"
                >
                  Create estimate
                </button>
              ) : null}
            </div>
          </div>

          <SyncInsuranceDataButton
            lastSyncedAt={estimate.last_synced_at}
            onSync={handleSync}
            disabled={triggerSync.isPending}
          />
        </div>

        {syncErrors !== null ? (
          <SyncErrorsBanner errors={syncErrors} />
        ) : hasSavedEstimate ? (
          <>
            <div className="text-charcoal-50">
              <i className="fa-sharp fa-solid fa-id-card text-[20px]" aria-hidden />
              <span className="text-sm font-medium ml-2">
                Type: {insuranceTypeLabel(estimate.estimate_type)} · Source: {estimate.source}
              </span>
            </div>

            <EstimateDisplay estimate={estimate} />

            {showSendButton && estimate.sendable_to_client && (
              <SendToFamilyButton clientId={clientId} estimate={estimate} />
            )}
          </>
        ) : (
          <div className="grid gap-2 my-6 text-charcoal-50 text-center">
            <img
              src="https://storage.googleapis.com/cartwheel-assets/empty_state.svg"
              className="m-auto"
              alt="Empty state"
            />
            <div className="font-medium">No estimate details yet</div>
            <div className="text-sm">
              Sync insurance data or select the Create Estimate link to get started.
            </div>
          </div>
        )}
      </Card.Root>

      {modalOpen && (
        <CostEstimateModal
          clientId={clientId}
          estimate={estimate}
          mode={editorMode}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setSyncErrors(null)
            setModalOpen(false)
          }}
          onInteraction={onInteraction}
        />
      )}
    </>
  )
}
